/**
 * FLOTTENWEIT IDENTISCH — nicht pro App editieren (Regel #24).
 * Quelle: 121apps-foundation/packages/ui/SprachLinks.tsx
 *
 * „Diese Seite in anderen Sprachen“ — dieselbe Seite in jeder indexierbaren
 * Sprachfassung, als echte `<a href>` im Server-HTML.
 *
 * Warum es den Block gibt (ROLLOUT „Von der deutschen Seite zu allen
 * Sprachen“, Punkt 2): Der Umschalter in der Kopfzeile ist in jeder App ein
 * Knopf mit Dialog. Ein Crawler ohne JavaScript fand darüber keine einzige
 * andere Sprache — gemessen am 14.10.2026: 42 von 42 Sitemap-Seiten in
 * 121surveys, 252 in paperball.finance. KI-Crawler (GPTBot, ClaudeBot,
 * PerplexityBot) werten weder hreflang noch Sitemap-Alternativen verlässlich
 * aus, sie folgen Links.
 *
 * Am selben Tag viermal unabhängig gebaut (121suite `Sprachlinks.tsx`,
 * 121assist `SprachLinks.tsx`, 121surveys `SprachFassungen.tsx`, 121vendors im
 * `MarketLanguageSelector`) — das hier ist die eine Fassung.
 *
 * Die Ziele kommen als `fassungen` aus `sprachFassungen()` (`seoKopf.ts`) —
 * derselben Regel, die hreflang und Sitemap schreibt. Ein Link, den hreflang
 * nicht kennt, oder eine hreflang-Fassung ohne Link kann es so nicht geben.
 *
 * Bewusst rein: kein Router (eine andere Sprache ist eine andere Adresse, die
 * der Server rendert — also `<a href>`, kein Router-Link), kein i18n (`titel`
 * als Prop), kein `window`, kein Zustand. Dieselbe Datei rendert als
 * Server-Komponente und in der SPA.
 */
import { useId, type MouseEvent, type ReactNode } from 'react'

import { nachSprache, type Ort, type SprachFassung } from './seoKopf'

export interface SprachLinksProps {
  /** Ergebnis von `sprachFassungen(config, rest)` — nicht selbst zusammenstellen. */
  fassungen: SprachFassung[]
  /** Die Fassung dieser Seite (wird mit `aria-current="page"` markiert); `null` = keine. */
  aktuell: Ort | null
  /** Überschrift des Blocks, schon übersetzt („Diese Seite in anderen Sprachen“). */
  titel: ReactNode
  /**
   * - `liste`   (Vorgabe) ein Eintrag je Fassung: Flagge, Land · Sprache
   *             (121suite, 121assist)
   * - `gruppen` eine Zeile je Sprache, darin die Länder (121surveys) — kürzer,
   *             sobald eine Sprache in vielen Ländern läuft
   */
  darstellung?: 'liste' | 'gruppen'
  /** Ebene der Überschrift. Vorgabe `h2`. */
  titelEbene?: 'h2' | 'h3' | 'h4'
  /**
   * Wird bei einem NORMALEN Klick gerufen (linke Taste, ohne Strg/⌘/Umschalt/
   * Alt) — z. B. um die Wahl vorher ins Cookie zu schreiben (Vokabero) oder
   * sie über `PUT /api/i18n/locale` zu speichern (121vendors). Ein Klick mit
   * Modifikator (neuer Tab) bleibt Sache des Browsers. Wer selbst navigiert,
   * ruft `event.preventDefault()`.
   */
  beiWahl?: (fassung: SprachFassung, event: MouseEvent<HTMLAnchorElement>) => void
  /** Unter so vielen Fassungen erscheint nichts (Vorgabe 2 — mit einer gibt es nichts zu verlinken). */
  mindestens?: number
  /** Klassen — jede App behält ihre Optik. Ohne Angabe: schlichte Tailwind-Vorgaben. */
  className?: string
  innenClassName?: string
  titelClassName?: string
  listeClassName?: string
  linkClassName?: string
  aktuellClassName?: string
}

const VORGABE = {
  nav: 'border-t border-slate-200',
  innen: 'mx-auto max-w-6xl px-4 py-6 sm:px-6',
  titel: 'mb-3 text-overline font-semibold uppercase tracking-wider text-slate-500',
  liste: 'flex flex-wrap gap-x-5 gap-y-2 text-base',
  link: 'text-slate-600 hover:underline',
  aktuell: 'font-semibold',
}

function istNormalerKlick(e: MouseEvent<HTMLAnchorElement>): boolean {
  return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey
}


export function SprachLinks({
  fassungen,
  aktuell,
  titel,
  darstellung = 'liste',
  titelEbene = 'h2',
  beiWahl,
  mindestens = 2,
  className,
  innenClassName,
  titelClassName,
  listeClassName,
  linkClassName,
  aktuellClassName,
}: SprachLinksProps) {
  const titelId = useId()
  if (fassungen.length < mindestens) return null

  const Titel = titelEbene
  const istAktuell = (f: SprachFassung) => aktuell !== null && f.land === aktuell.land && f.sprache === aktuell.sprache
  const link = (f: SprachFassung, text: ReactNode) => {
    const hier = istAktuell(f)
    return (
      <a
        href={f.pfad}
        hrefLang={f.hreflang}
        lang={f.sprache}
        aria-current={hier ? 'page' : undefined}
        className={`${linkClassName ?? VORGABE.link}${hier ? ` ${aktuellClassName ?? VORGABE.aktuell}` : ''}`}
        onClick={
          beiWahl
            ? (e) => {
                if (istNormalerKlick(e)) beiWahl(f, e)
              }
            : undefined
        }
      >
        <span aria-hidden="true">{f.flagge}</span> {text}
      </a>
    )
  }

  return (
    <nav aria-labelledby={titelId} className={className ?? VORGABE.nav}>
      <div className={innenClassName ?? VORGABE.innen}>
        <Titel id={titelId} className={titelClassName ?? VORGABE.titel}>
          {titel}
        </Titel>
        <ul className={listeClassName ?? VORGABE.liste}>
          {darstellung === 'gruppen'
            ? nachSprache(fassungen).map((g) => (
                <li key={g.sprache} lang={g.sprache}>
                  <span>{g.name}:</span>{' '}
                  {g.fassungen.map((f, i) => (
                    <span key={f.hreflang}>
                      {i > 0 ? ' ' : null}
                      {link(f, f.landName)}
                    </span>
                  ))}
                </li>
              ))
            : fassungen.map((f) => <li key={f.hreflang}>{link(f, `${f.landName} · ${f.sprachName}`)}</li>)}
        </ul>
      </div>
    </nav>
  )
}
